/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as ApplePayLogo } from '../../../assets/icons/payment-apple.svg';
import { ReactComponent as VisaLogo } from '../../../assets/icons/payment-visa.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import currencyCodesList from '../../../enum/CurrencyCodesList';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SelectField from '../../../shared-components/select-field/SelectField';
import { getCountryName, ISO_COUNTRY_CODES } from '../../../shared-functions/CountryCodes';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

export interface ReviewData {
  gatewayName: string;
  gatewayLogo: string;
  status: string;
  details: { label: string; value: string }[];
}

export interface EditGatewayData {
  configId?: string;
  _id?: string;
  id?: string;
  key: string;
  businessType?: string | string[];
  country?: string;
  paymentMethods?: string[];
  credentials?: Record<string, any>;
  currencies?: string[];
  currency?: string;
  defaultCurrency?: string;
  isDefault?: boolean;
  platformFee?: number | string;
  platformFeeByCurrency?: { currency: string; amount: number | string }[];
  platformFeeType?: 'percentage' | 'currency' | 'amount';
  paymentSurcharge?: number | string;
  paymentSurchargeType?: 'percentage';
  mode?: string;
  status?: string;
}

interface AddGatewayProperties {
  onCancel?: () => void;
  onSuccess?: (action: 'add' | 'edit') => void;
  editData?: EditGatewayData | null;
}

/**
 * Radio Button Value
 * @param {any} value - The value
 * @returns {any} The result
 */
const getRadioButtonValue = (value: any): string => {
  if (!value) return 'b2c';
  if (Array.isArray(value)) {
    if (value.includes('b2c') && value.includes('b2b')) {
      return 'both';
    }
    return value[0] || 'b2c';
  }
  return value;
};

/**
 * Business Type Label helper
 * @param {any} type - The type
 * @param {any} t - The translation function
 * @returns {string} The formatted business type label string
 */
const getBusinessTypeLabel = (type: any, t: any): string => {
  if (Array.isArray(type)) {
    return type
      .map((item) => {
        if (item.toLowerCase() === 'b2c') return t('b2cPayment');
        if (item.toLowerCase() === 'b2b') return t('b2bPayout');
        return item;
      })
      .join(', ');
  }
  const typeString = String(type).toLowerCase();
  if (typeString === 'b2c') return t('b2cPayment');
  if (typeString === 'b2b') return t('b2bPayout');
  return String(type);
};

/**
 * Mada logo component
 * @returns {any} React node
 */
function MadaLogoComponent() {
  return (
    <div className="mada-logo-inline method-logo-svg" title="Mada">
      mada
    </div>
  );
}

/**
 * Gets the state field key for a configuration field scoped to a payment method
 * @param {string} method - The payment method (e.g., 'credit_card', 'debit_card')
 * @param {string} fieldKey - The original configuration field key
 * @returns {string} The scoped field key
 */
const getPaymentMethodFieldKey = (method: string, fieldKey: string): string => {
  if (method === 'debit_card') {
    if (fieldKey.toLowerCase().includes('debitcard')) {
      return fieldKey;
    }
    return `${fieldKey}DebitCard`;
  }
  return fieldKey;
};

/**
 * Gets the input label for a configuration field scoped to a payment method
 * @param {string} method - The payment method (e.g., 'credit_card', 'debit_card')
 * @param {string} fieldKey - The configuration field key
 * @param {string} defaultLabel - The original configuration field label
 * @param {any} t - The translation function
 * @returns {string} The scoped field label
 */
const getPaymentMethodFieldLabel = (
  method: string,
  fieldKey: string,
  defaultLabel: string,
  t: any,
): string => {
  const hasCardSuffix = /\s*\(\s*Card\s*\)/i.test(defaultLabel);
  const baseLabel = defaultLabel.replace(/\s*\(\s*Card\s*\)/i, '');

  const translatedBase = t(fieldKey, { defaultValue: baseLabel });

  if (method === 'debit_card') {
    return `${translatedBase} (${t('debitCard')})`;
  }

  if (hasCardSuffix) {
    return `${translatedBase} (${t('card')})`;
  }

  return translatedBase;
};

/**
 * Add Gateway Flow Container
 * @param {any} props The component props
 * @param {any} props.onCancel The function to cancel the flow
 * @param {any} props.onSuccess - The onSuccess prop
 * @param {any} props.editData Optional data for edit flow
 * @returns {any} React node
 */
function AddGateway({
  onCancel = () => {},
  onSuccess = () => {},
  editData = null,
}: AddGatewayProperties) {
  const { t, i18n } = useTranslation('paymentGateway');
  const { activePortal } = usePortal();
  const isCPO = activePortal === PortalType.NETWORKS;

  const dispatch = useAppDispatch();
  const [step, setStep] = useState<number>(1);
  const [gatewayOptions, setGatewayOptions] = useState<any[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<string>(editData ? editData.key : '');
  const [selectedGatewayData, setSelectedGatewayData] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    if (editData) {
      const editBusinessType = getRadioButtonValue(editData.businessType).toLowerCase();
      let businessType: string | string[] = editBusinessType;
      if (isCPO) {
        businessType = editBusinessType === 'both' ? ['b2c', 'b2b'] : [editBusinessType];
      }
      const platformFeeByCurrencyMap: Record<string, string | number> = {};
      if (editData.platformFeeByCurrency && Array.isArray(editData.platformFeeByCurrency)) {
        editData.platformFeeByCurrency.forEach((item) => {
          platformFeeByCurrencyMap[item.currency] = item.amount;
        });
      } else if (
        editData.platformFeeByCurrency &&
        typeof editData.platformFeeByCurrency === 'object'
      ) {
        Object.entries(editData.platformFeeByCurrency).forEach(([currencyCode, amt]) => {
          platformFeeByCurrencyMap[currencyCode] = amt as string | number;
        });
      } else if (editData.platformFee !== undefined && editData.platformFee !== '') {
        const currenciesList =
          editData.currencies || (editData.currency ? [editData.currency] : []);
        currenciesList.forEach((currency) => {
          platformFeeByCurrencyMap[currency] = editData.platformFee as string | number;
        });
      }

      let initialPlatformFee: string | number = '';
      if (editData.platformFeeType === 'percentage') {
        initialPlatformFee = editData.platformFee ?? '';
        if (
          !initialPlatformFee &&
          editData.platformFeeByCurrency &&
          Array.isArray(editData.platformFeeByCurrency) &&
          editData.platformFeeByCurrency.length > 0
        ) {
          initialPlatformFee = editData.platformFeeByCurrency[0].amount;
        }
      }

      const editCurrencies = editData.currencies || (editData.currency ? [editData.currency] : []);
      const editDefaultCurrency =
        editData.defaultCurrency || editData.currency || editCurrencies[0] || '';

      return {
        status: editData.status || 'active',
        businessType,
        country: editData.country || '',
        paymentMethods: editData.paymentMethods || [],
        currencies: editCurrencies,
        defaultCurrency: editDefaultCurrency,
        platformFeeByCurrency: platformFeeByCurrencyMap,
        platformFee: initialPlatformFee,
        platformFeeType: editData.platformFeeType ?? 'percentage',
        paymentSurcharge: editData.paymentSurcharge ?? '',
        mode: editData.mode || 'live',
      };
    }
    return {
      status: 'active',
      businessType: isCPO ? ['b2c'] : 'b2c',
      country: '',
      paymentMethods: [],
      currencies: [],
      defaultCurrency: '',
      platformFeeByCurrency: {},
      platformFee: '',
      platformFeeType: 'percentage',
      paymentSurcharge: '',
      mode: 'live',
    };
  });
  const [isInitialEdit, setIsInitialEdit] = useState<boolean>(!!editData);

  const countryOptions = useMemo(() => {
    return ISO_COUNTRY_CODES.map((country) => ({
      label: `${country.value} - ${getCountryName(country.value, t)}`,
      value: country.value,
    }));
  }, [t]);

  /**
   * fetches the list of available payment gateways from the API and sets the options for the gateway selection dropdown
   */
  const getGatewayList = async () => {
    if (!formData.businessType) {
      return;
    }

    try {
      if (isInitialEdit) {
        setIsInitialEdit(false);
      } else {
        setSelectedGateway('');
        setSelectedGatewayData(null);
        setFormData((previous) => ({
          status: previous.status,
          businessType: previous.businessType,
          country: previous.country,
          paymentMethods: [],
          currencies: [],
          defaultCurrency: '',
          platformFeeByCurrency: previous.platformFeeByCurrency,
          platformFee: previous.platformFee,
          platformFeeType: previous.platformFeeType,
          paymentSurcharge: previous.paymentSurcharge,
          mode: previous.mode,
        }));
      }

      const response = await paymentGatewayApi.fetchPaymentGatewaysList({
        country: formData.country || 'SA',
        useCase: formData.businessType,
      });

      console.log('gateway list', response.data);

      const gateways: any[] = response.data || [];
      setGatewayOptions(gateways);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * fetches the details of a selected payment gateway based on the gateway key
   * It makes an API call to retrieve the gateway details, including supported payment methods, transaction types, and configuration fields
   * The retrieved data is stored in the selectedGatewayData state for use in the configuration step
   * @param {any} gatewayKey - The gatewayKey
   */
  const getGatewayDetails = async (gatewayKey: string) => {
    try {
      const gatewayData = gatewayOptions.find((gateway: any) => gateway.key === gatewayKey);

      if (!gatewayData) {
        return;
      }

      setSelectedGatewayData(gatewayData);

      if (gatewayData?.configFields) {
        const initialValues: Record<string, string> = {};

        gatewayData.configFields.forEach((field: any) => {
          let savedValue = '';
          if (editData && editData.credentials) {
            const normalizedKey = field.key.toLowerCase().replace(/_/g, '');
            const matchingEntry = Object.entries(editData.credentials).find(
              ([k]) => k.toLowerCase().replace(/_/g, '') === normalizedKey,
            );
            if (matchingEntry) {
              savedValue = String(matchingEntry[1]);
            }
          }
          initialValues[field.key] = savedValue;

          if (field.requiredFor && field.requiredFor.includes('debit_card')) {
            const debitKey = getPaymentMethodFieldKey('debit_card', field.key);
            if (debitKey !== field.key) {
              let debitSavedValue = '';
              if (editData && editData.credentials) {
                const normalizedDebitKey = debitKey.toLowerCase().replace(/_/g, '');
                const matchingDebitEntry = Object.entries(editData.credentials).find(
                  ([k]) => k.toLowerCase().replace(/_/g, '') === normalizedDebitKey,
                );
                if (matchingDebitEntry) {
                  debitSavedValue = String(matchingDebitEntry[1]);
                } else if (editData.paymentMethods?.includes('debit_card')) {
                  debitSavedValue = savedValue;
                }
              }
              initialValues[debitKey] = debitSavedValue;
            }
          }
        });

        setFormData((previous) => ({
          ...previous,
          ...initialValues,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Select Change
   * @param {string} name - The name of the field
   * @param {string} value - The selected value
   */
  const handleSelectChange = async (name: string, value: string) => {
    if (name === 'gateway') {
      setSelectedGateway(value);
      await getGatewayDetails(value);
      const targetGateway = gatewayOptions.find((gateway: any) => gateway.key === value);
      const supportedCurrencies: string[] = targetGateway?.supportedCurrencies || [];
      if (supportedCurrencies.length > 0) {
        setFormData((previous) => {
          const previousCurrencies = (previous.currencies as string[]) || [];
          const filteredCurrencies = previousCurrencies.filter((currencyCode) =>
            supportedCurrencies.includes(currencyCode),
          );
          return {
            ...previous,
            currencies: filteredCurrencies,
            defaultCurrency:
              previous.defaultCurrency && filteredCurrencies.includes(previous.defaultCurrency)
                ? previous.defaultCurrency
                : filteredCurrencies[0] || '',
          };
        });
      }
    }
  };

  /**
   * Handle currencies selection change
   * @param {string} name - The name of the field
   * @param {string[]} values - The selected currency values
   */
  const handleCurrenciesChange = (name: string, values: string[]) => {
    setFormData((previous) => ({
      ...previous,
      [name]: values,
      defaultCurrency:
        !editData || !previous.defaultCurrency || !values.includes(previous.defaultCurrency)
          ? values[0] || ''
          : previous.defaultCurrency,
    }));
  };

  const currencyOptions = useMemo(() => {
    if (selectedGatewayData?.supportedCurrencies?.length) {
      return selectedGatewayData.supportedCurrencies.map((currencyCode: string) => ({
        label: currencyCode,
        value: currencyCode,
      }));
    }
    return currencyCodesList;
  }, [selectedGatewayData]);

  /**
   * handleInputChange
   * @param {string} name - The name of the field
   * @param {any} value - The input value
   */
  const handleInputChange = (name: string, value: string | number | string[]) => {
    if (name === 'businessType') {
      const arrayValue = value === 'both' ? ['b2c', 'b2b'] : [value as string];
      setFormData((previous) => ({
        ...previous,
        [name]: isCPO ? arrayValue : (value as string),
      }));
    } else if (name.startsWith('platformFee_')) {
      const currency = name.replace('platformFee_', '');
      setFormData((previous) => ({
        ...previous,
        platformFeeByCurrency: {
          ...(previous.platformFeeByCurrency as Record<string, any>),
          [currency]: value,
        },
      }));
    } else {
      setFormData((previous) => ({ ...previous, [name]: value }));
    }
  };

  useEffect(() => {
    if (formData.businessType) {
      getGatewayList();
    }
  }, [formData.country, formData.businessType]);

  useEffect(() => {
    if (editData) {
      setIsInitialEdit(true);
      const editBusinessType = getRadioButtonValue(editData.businessType).toLowerCase();
      let businessTypeValue: string | string[] = editBusinessType;
      if (isCPO) {
        businessTypeValue = editBusinessType === 'both' ? ['b2c', 'b2b'] : [editBusinessType];
      }

      const platformFeeByCurrencyMap: Record<string, string | number> = {};
      if (editData.platformFeeByCurrency && Array.isArray(editData.platformFeeByCurrency)) {
        editData.platformFeeByCurrency.forEach((item) => {
          platformFeeByCurrencyMap[item.currency] = item.amount;
        });
      } else if (
        editData.platformFeeByCurrency &&
        typeof editData.platformFeeByCurrency === 'object'
      ) {
        Object.entries(editData.platformFeeByCurrency).forEach(([currencyCode, amt]) => {
          platformFeeByCurrencyMap[currencyCode] = amt as string | number;
        });
      } else if (editData.platformFee !== undefined && editData.platformFee !== '') {
        const currenciesList =
          editData.currencies || (editData.currency ? [editData.currency] : []);
        currenciesList.forEach((currency) => {
          platformFeeByCurrencyMap[currency] = editData.platformFee as string | number;
        });
      }

      let initialPlatformFee: string | number = '';
      if (editData.platformFeeType === 'percentage') {
        initialPlatformFee = editData.platformFee ?? '';
        if (
          !initialPlatformFee &&
          editData.platformFeeByCurrency &&
          Array.isArray(editData.platformFeeByCurrency) &&
          editData.platformFeeByCurrency.length > 0
        ) {
          initialPlatformFee = editData.platformFeeByCurrency[0].amount;
        }
      }

      const editCurrencies = editData.currencies || (editData.currency ? [editData.currency] : []);
      const editDefaultCurrency =
        editData.defaultCurrency || editData.currency || editCurrencies[0] || '';

      setFormData((previous) => ({
        ...previous,
        country: editData.country || '',
        businessType: businessTypeValue,
        paymentMethods: editData.paymentMethods || [],
        currencies: editCurrencies,
        defaultCurrency: editDefaultCurrency,
        platformFeeByCurrency: platformFeeByCurrencyMap,
        platformFee: initialPlatformFee,
        platformFeeType: editData.platformFeeType ?? 'percentage',
        paymentSurcharge: editData.paymentSurcharge ?? '',
        mode: editData.mode || 'live',
      }));
      setSelectedGateway(editData.key);
      setStep(1);
    }
  }, [editData, isCPO]);

  useEffect(() => {
    if (editData && gatewayOptions.length > 0 && !selectedGatewayData) {
      getGatewayDetails(editData.key);
    }
  }, [editData, gatewayOptions, selectedGatewayData]);

  /**
   * Validate if all required fields in step 1 are entered
   * @returns {boolean} True if step 1 is valid
   */
  const isStep1Valid = (): boolean => {
    if (!selectedGateway) {
      return false;
    }

    const selectedCurrencies = (formData.currencies as string[]) || [];
    if (selectedCurrencies.length === 0) {
      return false;
    }

    if (formData.platformFeeType === 'amount') {
      const isEveryCurrencyEntered = selectedCurrencies.every((currencyCode) => {
        const value = (formData.platformFeeByCurrency as Record<string, any>)?.[currencyCode];
        return (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== '' &&
          !Number.isNaN(Number(value)) &&
          Number(value) >= 0
        );
      });

      if (!isEveryCurrencyEntered) {
        return false;
      }
    }

    if (formData.platformFeeType === 'percentage') {
      if (formData.platformFee !== '' && formData.platformFee !== undefined) {
        const feeNumber = Number(formData.platformFee);
        if (Number.isNaN(feeNumber) || feeNumber < 0 || feeNumber > 100) {
          return false;
        }
      }
    }

    if (formData.paymentSurcharge !== '' && formData.paymentSurcharge !== undefined) {
      const surchargeNumber = Number(formData.paymentSurcharge);
      if (Number.isNaN(surchargeNumber) || surchargeNumber < 0 || surchargeNumber > 100) {
        return false;
      }
    }

    return true;
  };

  /**
   * Advance from step 1 to step 2
   */
  const handleNextStep1 = () => {
    if (isStep1Valid()) setStep(2);
  };
  /**
   * Navigate back from step 2 to step 1
   */
  const handleBackStep2 = () => setStep(1);
  /**
   * Advance from step 2 to step 3
   */
  const handleNextStep2 = () => setStep(3);
  /**
   * Navigate back from step 3 to step 2
   */
  const handleBackStep3 = () => setStep(2);

  /**
   * Build the configuration payload from the current form data
   * @param {string} [overrideStatus] - Optional status override
   * @returns {Record<string, any>} Gateway configuration payload
   */
  const buildPayload = (overrideStatus?: string): Record<string, any> => {
    const credentials: Record<string, string> = {};

    selectedGatewayData?.configFields
      ?.filter((field: any) => !field.requiredFor || field.requiredFor.length === 0)
      ?.forEach((field: any) => {
        if (formData[field.key] !== undefined && formData[field.key] !== '') {
          credentials[field.key] = String(formData[field.key]);
        }
      });

    (formData.paymentMethods as string[])?.forEach((method: string) => {
      const fields = selectedGatewayData?.configFields?.filter((field: any) =>
        field.requiredFor?.includes(method),
      );
      fields?.forEach((field: any) => {
        const fieldKey = getPaymentMethodFieldKey(method, field.key);
        if (formData[fieldKey] !== undefined && formData[fieldKey] !== '') {
          credentials[fieldKey] = String(formData[fieldKey]);
          if (fieldKey !== field.key && !credentials[field.key]) {
            credentials[field.key] = String(formData[fieldKey]);
          }
        }
      });
    });

    let finalPlatformFeeByCurrencyArray: { currency: string; amount: number }[] | undefined;
    let finalPlatformFee: number | undefined;

    const selectedCurrencies = (formData.currencies as string[]) || [];

    if (formData.platformFeeType === 'percentage') {
      finalPlatformFee =
        formData.platformFee !== '' && formData.platformFee !== undefined
          ? Number(formData.platformFee)
          : undefined;
    } else {
      finalPlatformFeeByCurrencyArray = selectedCurrencies.map((currency) => {
        const amount = (formData.platformFeeByCurrency as Record<string, any>)?.[currency];
        return {
          currency,
          amount: Number(amount ?? 0),
        };
      });
    }

    const hasSurcharge =
      formData.paymentSurcharge !== '' &&
      formData.paymentSurcharge !== undefined &&
      !Number.isNaN(Number(formData.paymentSurcharge));

    const defaultCurrency =
      (!editData
        ? selectedCurrencies[0]
        : formData.defaultCurrency || editData.defaultCurrency || selectedCurrencies[0]) ||
      selectedGatewayData?.supportedCurrencies?.[0] ||
      'SAR';

    return {
      gatewayKey: selectedGatewayData?.key,
      currency: defaultCurrency,
      defaultCurrency,
      isDefault: overrideStatus !== 'inactive',
      status: overrideStatus || formData.status || 'active',
      currencies: selectedCurrencies,
      platformFee: formData.platformFeeType === 'percentage' ? finalPlatformFee : undefined,
      platformFeeByCurrency:
        formData.platformFeeType === 'amount' ? finalPlatformFeeByCurrencyArray : undefined,
      platformFeeType: formData.platformFeeType,
      paymentSurcharge: hasSurcharge ? Number(formData.paymentSurcharge) : undefined,
      paymentSurchargeType: hasSurcharge ? 'percentage' : undefined,
      country: formData.country || selectedGatewayData?.supportedCountries?.[0] || 'SA',
      useCases: isCPO
        ? (formData.businessType as string[])
        : [String(formData.businessType).toLowerCase()],
      paymentMethods: formData.paymentMethods,
      mode: formData.mode,
      credentials,
    };
  };

  /**
   * Handle Save Configuration button click on Review Step
   * @returns {Promise<void>}
   */
  const handleSave = async (): Promise<void> => {
    if (editData) {
      const configId = editData.configId || editData._id || editData.id;
      if (!configId) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('failedToSaveConfiguration'),
            type: 'error',
          }),
        );
        return;
      }
      try {
        const payload = buildPayload();
        await paymentGatewayApi.updatePaymentGateway(configId, payload);
        if (onSuccess) {
          onSuccess('edit');
        }
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            status: true,
            message:
              error?.response?.data?.message || error?.message || t('failedToSaveConfiguration'),
            type: 'error',
          }),
        );
      }
    } else {
      setShowConfirmModal(true);
    }
  };

  /**
   * Confirm activating the gateway and setting currency as default
   * @returns {Promise<void>}
   */
  const handleConfirmActivateAndSave = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload('active');
      const createResponse = await paymentGatewayApi.createPaymentGateway(payload);
      const createdConfigId =
        createResponse?.data?.configId ||
        createResponse?.data?._id ||
        createResponse?.data?.id ||
        createResponse?.configId ||
        createResponse?._id ||
        createResponse?.id;

      if (createdConfigId) {
        // 1. Activate the payment gateway
        const activationPayload = isCPO ? { useCases: payload.useCases } : undefined;
        await paymentGatewayApi.updatePaymentGatewayStatus(
          'activate',
          createdConfigId,
          activationPayload,
        );

        // 2. Set default currency
        if (payload.defaultCurrency) {
          await paymentGatewayApi.setDefaultCurrency(createdConfigId, {
            defaultCurrency: payload.defaultCurrency,
          });
        }
      }

      setShowConfirmModal(false);
      if (onSuccess) {
        onSuccess('add');
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || error?.message || t('failedToSaveConfiguration'),
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Save the gateway as inactive without activating or setting default currency
   * @returns {Promise<void>}
   */
  const handleSaveAsInactive = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload('inactive');
      await paymentGatewayApi.createPaymentGateway(payload);

      setShowConfirmModal(false);
      if (onSuccess) {
        onSuccess('add');
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || error?.message || t('failedToSaveConfiguration'),
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Validate if all required fields in step 2 are entered
   * @returns {boolean} True if valid
   */
  const isStep2Valid = (): boolean => {
    if (!selectedGatewayData?.configFields) {
      return true;
    }

    const commonFieldsValid = selectedGatewayData.configFields
      .filter((field: any) => !field.requiredFor || field.requiredFor.length === 0)
      .every((field: any) => {
        if (!field.required) {
          return true;
        }
        const value = formData[field.key];
        return value !== undefined && value !== null && String(value).trim() !== '';
      });

    if (!commonFieldsValid) {
      return false;
    }

    const selectedMethods = (formData.paymentMethods as string[]) || [];
    return selectedMethods.every((method: string) => {
      const fields = selectedGatewayData.configFields.filter((field: any) =>
        field.requiredFor?.includes(method),
      );

      return fields.every((field: any) => {
        if (!field.required) {
          return true;
        }
        const fieldKey = getPaymentMethodFieldKey(method, field.key);
        const value = formData[fieldKey];
        return value !== undefined && value !== null && String(value).trim() !== '';
      });
    });
  };

  /**
   * buildReviewData constructs the data structure needed for the review step based on the selected gateway and form data
   * @returns {any} ReviewData object containing gateway name, logo, status, and configuration details for display in the review step
   */
  const buildReviewData = (): ReviewData => {
    const details: any[] = [];

    // 1. Common configuration fields
    selectedGatewayData?.configFields
      ?.filter((field: any) => !field.requiredFor || field.requiredFor.length === 0)
      ?.forEach((field: any) => {
        details.push({
          label: t(field.key, { defaultValue: field.label }),
          value: field.secret ? '*************' : String(formData[field.key] || ''),
        });
      });

    // 2. Method-specific configuration fields
    const selectedMethods = (formData.paymentMethods as string[]) || [];
    selectedMethods.forEach((method: string) => {
      const fields = selectedGatewayData?.configFields?.filter((field: any) =>
        field.requiredFor?.includes(method),
      );

      fields?.forEach((field: any) => {
        if (field.group && Array.isArray(field.fields)) {
          field.fields.forEach((subField: any) => {
            const subFieldKey = getPaymentMethodFieldKey(method, subField.key);
            const subFieldLabel = getPaymentMethodFieldLabel(
              method,
              subField.key,
              subField.label,
              t,
            );
            details.push({
              label: subFieldLabel,
              value: subField.secret ? '*************' : String(formData[subFieldKey] || ''),
            });
          });
        } else {
          const fieldKey = getPaymentMethodFieldKey(method, field.key);
          const fieldLabel = getPaymentMethodFieldLabel(method, field.key, field.label, t);
          details.push({
            label: fieldLabel,
            value: field.secret ? '*************' : String(formData[fieldKey] || ''),
          });
        }
      });
    });

    const logoMap: Record<string, string> = {
      stripe: 'S',
      hyperpay: 'hp',
    };

    return {
      gatewayName: selectedGatewayData?.displayName || '',
      gatewayLogo: logoMap[selectedGateway] || 'G',
      status: formData.status === 'active' ? t('active') : t('inactive'),
      details,
    };
  };

  const reviewData = buildReviewData();

  return (
    <div className={`gateway-main-container ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      {/* COMMON HEADER */}
      <div className="header">
        <h2 className="title">{editData ? t('') : t('configureGateway')}</h2>
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="add-gateway-container">
          <div className="stepper-container">
            <div className="step-active">
              <span className="step-number">1</span>
              {t('selectGateway')}
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">2</span>
              {t('configure')}
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">3</span> {t('review')}
            </div>
          </div>
          <div className="form-content">
            <div className="form-content-header">
              <h3 className="section-title">{t('gatewayDetails')}</h3>
              <p className="section-subtitle">{t('selectGatewayToConfigure')}</p>
            </div>
            <div className="form-container">
              <div className="business-type-radio-wrapper">
                <RadioButton
                  inputLabel={t('businessType')}
                  name="businessType"
                  value={getRadioButtonValue(formData.businessType)}
                  list={
                    isCPO
                      ? [
                          { label: t('b2cPayment'), value: 'b2c' },
                          { label: t('b2bPayout'), value: 'b2b' },
                          { label: t('both'), value: 'both' },
                        ]
                      : [
                          { label: t('b2cPayment'), value: 'b2c' },
                          { label: t('b2bPayout'), value: 'b2b' },
                        ]
                  }
                  onChangeValue={handleInputChange}
                />
              </div>
              <div className="selectFields-row">
                <SelectField
                  inputLabel={t('country')}
                  name="country"
                  options={countryOptions}
                  placeholder={t('selectCountry')}
                  selectedValue={formData.country as string}
                  onChangeValue={handleInputChange}
                  enableSearch
                />
              </div>
              <div className="selectFields-row">
                <SelectField
                  inputLabel={t('selectPaymentGateway')}
                  name="gateway"
                  options={gatewayOptions.map((gateway: any) => ({
                    value: gateway.key,
                    label: gateway.displayName,
                  }))}
                  placeholder={t('selectGateway')}
                  selectedValue={selectedGateway}
                  onChangeValue={handleSelectChange}
                />
              </div>
              <div className="selectFields-row">
                <MultiSelectDropdown
                  name="currencies"
                  title={t('currencies')}
                  placeholder={selectedGateway ? t('selectCurrencies') : t('selectGatewayFirst')}
                  placeholderFilled={t('currencies')}
                  options={currencyOptions}
                  selectedValues={(formData.currencies as string[]) || []}
                  onSelectionChange={handleCurrenciesChange}
                  search
                  disabled={!selectedGateway}
                  selectedChips
                />
              </div>
              <div className="selectFields-row platform-fee-container">
                {((formData.currencies as string[]) || []).length > 0 && (
                  <div className="business-type-radio-wrapper">
                    <RadioButton
                      inputLabel={t('type')}
                      name="platformFeeType"
                      value={formData.platformFeeType as string}
                      list={[
                        { label: t('percentage'), value: 'percentage' },
                        { label: t('amount'), value: 'amount' },
                      ]}
                      onChangeValue={handleInputChange}
                    />
                  </div>
                )}
                {formData.platformFeeType === 'percentage' &&
                  ((formData.currencies as string[]) || []).length > 0 && (
                    <div className="platform-fee-compound-field">
                      <div className="fee-input-wrap">
                        <CustomInput
                          inputLabel={t('platformFee')}
                          name="platformFee"
                          type="number"
                          placeholder={t('enterPlatformFee')}
                          value={formData.platformFee as string | number}
                          onChangeValue={handleInputChange}
                          suffixText="%"
                        />
                      </div>
                    </div>
                  )}
                {formData.platformFeeType === 'amount' &&
                  ((formData.currencies as string[]) || []).map((currencyCode) => (
                    <div className="platform-fee-compound-field" key={currencyCode}>
                      <div className="fee-input-wrap">
                        <CustomInput
                          inputLabel={`${t('platformFee')} (${currencyCode})`}
                          name={`platformFee_${currencyCode}`}
                          type="number"
                          placeholder={t('enterPlatformFee')}
                          value={
                            (formData.platformFeeByCurrency as Record<string, any>)?.[
                              currencyCode
                            ] ?? ''
                          }
                          onChangeValue={handleInputChange}
                          suffixText={currencyCode}
                          valueRequired
                        />
                      </div>
                    </div>
                  ))}
              </div>
              <div className="selectFields-row">
                <CustomInput
                  inputLabel={t('paymentSurcharge')}
                  name="paymentSurcharge"
                  type="number"
                  placeholder={t('enterPaymentSurcharge')}
                  value={formData.paymentSurcharge as string | number}
                  onChangeValue={handleInputChange}
                  suffixText="%"
                />
              </div>
              {selectedGatewayData && (
                <div className="payment-methods-selection-wrap">
                  <p className="input-label">{t('supportedPaymentMethods')}</p>

                  <div className="payment-methods-list">
                    {selectedGatewayData.supportedPaymentMethods.map((m: string) => {
                      const isSelected = (formData.paymentMethods as string[])?.includes(m);

                      const paymentMethodConfig: Record<
                        string,
                        {
                          label?: string;
                          logo?: React.ReactNode;
                        }
                      > = {
                        credit_card: {
                          label: t('card'),
                          logo: <CreditCardIcon className="method-logo-svg card-outline-icon" />,
                        },
                        debit_card: {
                          label: t('debitCard'),
                          logo: <VisaLogo className="method-logo-svg" />,
                        },
                        mada: {
                          label: t('mada'),
                          logo: <MadaLogoComponent />,
                        },
                        apple_pay: {
                          label: t('applePay'),
                          logo: <ApplePayLogo className="method-logo-svg" />,
                        },
                        google_pay: {
                          label: t('googlePay'),
                        },
                        stc_pay: {
                          label: t('stcPay'),
                        },
                      };

                      const config = paymentMethodConfig[m];

                      const label =
                        config?.label ||
                        m
                          .split('_')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ');

                      const firstLetter = label.charAt(0).toUpperCase();

                      /**
                       * Toggle payment method
                       */
                      const handleToggle = () => {
                        const currentMethods = (formData.paymentMethods as string[]) || [];

                        const newMethods = currentMethods.includes(m)
                          ? currentMethods.filter((item) => item !== m)
                          : [...currentMethods, m];

                        handleInputChange('paymentMethods', newMethods);
                      };

                      return (
                        <div
                          key={m}
                          className={`payment-method-select-box ${isSelected ? 'selected' : ''}`}
                          onClick={handleToggle}
                          role="presentation">
                          <div className="left-side">
                            {config?.logo ? (
                              config.logo
                            ) : (
                              <div className="method-logo-svg round-letter-logo">{firstLetter}</div>
                            )}

                            <span className="method-label">{label}</span>
                          </div>

                          {isSelected && (
                            <div className="check-indicator">
                              <span className="checkmark">✔</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="footer-actions">
            <MyButton label={t('cancel')} buttonType="outline" onClickFunc={onCancel} />
            <MyButton
              label={t('next')}
              buttonType="submit"
              disableButton={!isStep1Valid()}
              onClickFunc={handleNextStep1}
            />
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="configure-gateway-container">
          <div className="stepper-container">
            <div className="step-completed">
              <span className="step-icon">✔</span>
              {t('selectGateway')}
            </div>
            <div className="stepper-line-active" />
            <div className="step-active">
              <span className="step-number">2</span>
              {t('configure')}
            </div>
            <div className="stepper-line" />
            <div className="step-inactive">
              <span className="step-number">3</span>
              {t('review')}
            </div>
          </div>
          <div className="form-content-header">
            <h3 className="section-title">{t('configurationDetails')}</h3>
            <p className="section-subtitle">{t('enterRequiredDetailsForGatewayConfiguration')}</p>
          </div>
          <div className="form-content">
            <div className="form-container">
              {/* COMMON FIELDS */}
              <div className="form-fields-flex-row">
                <RadioButton
                  inputLabel={t('mode')}
                  name="mode"
                  value={Array.isArray(formData.mode) ? undefined : formData.mode}
                  list={[
                    { label: t('live'), value: 'live' },
                    { label: t('test'), value: 'test' },
                  ]}
                  onChangeValue={handleInputChange}
                />
                {selectedGatewayData?.configFields
                  ?.filter((field: any) => field.requiredFor.length === 0)
                  .map((field: any) => (
                    <div className="full-width-input-group" key={field.key}>
                      <CustomInput
                        inputLabel={t(field.key, { defaultValue: field.label })}
                        name={field.key}
                        type={field.type as 'text' | 'password' | 'email' | 'number'}
                        placeholder={field.placeholder}
                        value={formData[field.key] as string}
                        onChangeValue={handleInputChange}
                        valueRequired={field.required}
                      />
                    </div>
                  ))}
              </div>
              <div className="form-fields-flex-row">
                {/* DYNAMIC PAYMENT METHOD GROUPS */}
                {(formData.paymentMethods as string[])?.map((method: string) => {
                  const fields = selectedGatewayData?.configFields?.filter((field: any) =>
                    field.requiredFor.includes(method),
                  );

                  if (!fields?.length) {
                    return null;
                  }

                  const methodLabels: Record<string, string> = {
                    credit_card: t('card'),
                    debit_card: t('debitCard'),
                    mada: 'Mada',
                    apple_pay: 'Apple Pay',
                    google_pay: 'Google Pay',
                    stc_pay: 'STC Pay',
                  };

                  const displayGroupName =
                    methodLabels[method] ||
                    method
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                  return (
                    <div className="input-group-box" key={method}>
                      <h4 className="group-title">{displayGroupName}</h4>

                      {fields.map((field: any) => {
                        const fieldKey = getPaymentMethodFieldKey(method, field.key);
                        const fieldLabel = getPaymentMethodFieldLabel(
                          method,
                          field.key,
                          field.label,
                          t,
                        );

                        return (
                          <div className="input-group mb-16" key={fieldKey}>
                            <CustomInput
                              inputLabel={fieldLabel}
                              name={fieldKey}
                              type={field.type as 'text' | 'password' | 'email' | 'number'}
                              placeholder={field.placeholder}
                              value={(formData[fieldKey] as string) || ''}
                              onChangeValue={handleInputChange}
                              valueRequired={field.required}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="footer-actions">
            <MyButton label={t('back')} buttonType="outline" onClickFunc={handleBackStep2} />
            <MyButton
              label={t('next')}
              buttonType="submit"
              disableButton={!isStep2Valid()}
              onClickFunc={handleNextStep2}
            />
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <div className="review-gateway-container">
          <div className="stepper-container">
            <div className="step-completed">
              <div className="step-icon">✓</div>
              <span>{t('selectGateway')}</span>
            </div>
            <div className="stepper-line-active" />
            <div className="step-completed">
              <div className="step-icon">✓</div>
              <span>{t('configure')}</span>
            </div>
            <div className="stepper-line-active" />
            <div className="step-active">
              <div className="step-number">3</div>
              <span>{t('review')}</span>
            </div>
          </div>
          <div className="header-container">
            <h2 className="title">{t('reviewConfiguration')}</h2>
            <p className="subtitle">{t('reviewDetailsBeforeSaving')}</p>
          </div>
          <div className="form-content">
            <div className="form-container">
              <div className="review-details-card">
                <div className="review-details-header">
                  {reviewData.gatewayName} {t('gatewayDetails')}
                </div>
                <div className="details-list">
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('businessType')}</div>
                    <div className="review-detail-value">
                      {getBusinessTypeLabel(formData.businessType, t)}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('country')}</div>
                    <div className="review-detail-value">
                      {getCountryName(formData.country as string, t)}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('paymentMethod')}</div>
                    <div className="review-detail-value">
                      {(formData.paymentMethods as string[])
                        .map((m) => {
                          const paymentMethodLabels: Record<string, string> = {
                            credit_card: t('card'),
                            debit_card: t('debitCard'),
                            mada: 'Mada',
                            apple_pay: 'Apple Pay',
                            google_pay: 'Google Pay',
                            stc_pay: 'STC Pay',
                          };

                          return (
                            paymentMethodLabels[m] ||
                            m
                              .split('_')
                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                          );
                        })
                        .join(', ')}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('currencies')}</div>
                    <div className="review-detail-value">
                      {((formData.currencies as string[]) || []).join(', ') || '-'}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{`${t('default')} ${t('currency')}`}</div>
                    <div className="review-detail-value">
                      {formData.defaultCurrency ||
                        ((formData.currencies as string[]) || [])[0] ||
                        '-'}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('platformFee')}</div>
                    <div className="review-detail-value">
                      {(() => {
                        if (formData.platformFeeType === 'percentage') {
                          return formData.platformFee !== '' && formData.platformFee !== undefined
                            ? `${formData.platformFee}%`
                            : '-';
                        }
                        return (
                          ((formData.currencies as string[]) || [])
                            .map((currency) => {
                              const amount = (
                                formData.platformFeeByCurrency as Record<string, any>
                              )?.[currency];
                              if (amount === undefined || amount === '') return null;
                              return `${amount} ${currency}`;
                            })
                            .filter(Boolean)
                            .join(', ') || '-'
                        );
                      })()}
                    </div>
                  </div>
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('paymentSurcharge')}</div>
                    <div className="review-detail-value">
                      {formData.paymentSurcharge !== '' && formData.paymentSurcharge !== undefined
                        ? `${formData.paymentSurcharge}%`
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="review-details-card">
                <div className="review-details-header">{t('configurationDetails')}</div>
                <div className="details-list">
                  <div className="review-detail-row">
                    <div className="review-detail-label">{t('mode')}</div>
                    <div className="review-detail-value">
                      {formData.mode === 'live' ? t('live') : t('test')}
                    </div>
                  </div>
                  {reviewData.details.map((detail, index) => (
                    <div className="review-detail-row" key={index}>
                      <div className="review-detail-label">{detail.label}</div>
                      <div className="review-detail-value">{detail.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="configuration-btn">
                <MyButton
                  label="Test Configuration"
                  buttonType="primary-outline"
                  onClickFunc={() => { }}
                />
              </div> */}
            </div>
          </div>
          <div className="footer-actions">
            <div className="left-actions">
              <MyButton label={t('back')} buttonType="outline" onClickFunc={handleBackStep3} />
            </div>
            <div className="right-actions">
              <MyButton label={t('cancel')} buttonType="outline" onClickFunc={onCancel} />
              <MyButton
                label={t('saveConfiguration')}
                buttonType="submit"
                onClickFunc={handleSave}
              />
            </div>
          </div>
        </div>
      )}
      <ModalBox
        open={showConfirmModal}
        header
        title={t('activateGatewayConfirmationTitle', { defaultValue: 'Gateway Activation' })}
        closeModal={() => !isSubmitting && setShowConfirmModal(false)}
        content={
          <div className="gateway-activation-confirm-modal">
            <p className="confirm-desc">
              {t('gatewayActivationDesc', {
                defaultValue: `Do you want to activate ${selectedGatewayData?.displayName || ''} and set ${
                  ((formData.currencies as string[]) || [])[0] || ''
                } as the default currency?`,
                gateway: selectedGatewayData?.displayName || '',
                currency: ((formData.currencies as string[]) || [])[0] || '',
              })}
            </p>

            <div className="warning-box">
              <p className="warning-text">{t('activationWarning')}</p>
            </div>

            <div className="modal-actions">
              <MyButton
                label={t('cancel')}
                buttonType="outline"
                disableButton={isSubmitting}
                onClickFunc={() => setShowConfirmModal(false)}
              />
              <MyButton
                label={t('saveAsInactive', { defaultValue: 'Save as Inactive' })}
                buttonType="outline"
                disableButton={isSubmitting}
                onClickFunc={handleSaveAsInactive}
              />
              <MyButton
                label={t('activateAndSetDefault', { defaultValue: 'Activate & Set Default' })}
                buttonType="submit"
                disableButton={isSubmitting}
                onClickFunc={handleConfirmActivateAndSave}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}

AddGateway.defaultProps = {
  /**
   * onCancel default function
   */
  onCancel: () => {},
  /**
   * onCancel default function
   */
  onSuccess: () => {},
  editData: null,
};
export default AddGateway;
